import React, { useContext, useState, useEffect } from 'react';
import { Drawer, List, ListItem, ListItemText, AppBar, Toolbar, IconButton, ListItemButton, Badge } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { getAllReviewVoiceNotesForAdmin, getAllReviewUpgradedLARForAdmin } from '../service/service';

const drawerWidth = 240;

const navItems = [
  { label: 'Courses', path: '/dashboard' },
  { label: 'Review Voice Notes', path: '/review-voice-notes' },
  { label: 'Review Upgraded LAR', path: '/review-upgraded-lar' },
  { label: 'Review AI Feedbacks', path: '/review-ai-feedbacks' },
];

const DrawerMenu = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation(); // Get the current location
  const { logout } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0); // State to manage selected menu item
  const [counts, setCounts] = useState({ voiceNotes: 0, upgradedLar: 0 });

  useEffect(() => {
    const index = navItems.findIndex((item) => item.path === location.pathname);
    if (index !== -1) {
      setSelectedIndex(index);
    }
  }, [location]);

  // Fetch review counts when the drawer opens
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        // Fetch counts for both voice notes and upgraded LAR
        const [voiceResults, upgradedLarResults] = await Promise.all([
          getAllReviewVoiceNotesForAdmin(),
          getAllReviewUpgradedLARForAdmin(),
        ]);

        // Update state with the fetched counts
        setCounts({
          voiceNotes: voiceResults,
          upgradedLar: upgradedLarResults,
        });
      } catch (error) {
        console.error("Failed to fetch review counts:", error);
      }
    };

    fetchCounts();
  }, [open]);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleLogout = () => {
    logout();
    localStorage.removeItem('token');
    navigate('/'); // Redirect to login page
  };

  const navBadge = (path, label) => {
    const content =
      path === "/review-voice-notes"
        ? counts.voiceNotes
        : path === "/review-upgraded-lar"
          ? counts.upgradedLar
          : null;

    if (content === null) return label;
    return (
      <Badge
        badgeContent={content}
        color="secondary"
        sx={{ "& .MuiBadge-badge": { right: -20 } }}
      >
        <span>{label}</span>
      </Badge>
    );
  };

  return (
    <div style={{ display: 'flex' }}>
      <AppBar position="fixed">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={handleDrawerToggle}
            sx={{ mr: 1 }}
          >
            <Badge badgeContent={counts.voiceNotes + counts.upgradedLar} color="secondary">
              <MenuIcon />
            </Badge>
          </IconButton>
          <h5 style={{ flexGrow: 1 }}>Menu</h5>
        </Toolbar>
      </AppBar>

      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        variant="temporary"
        anchor="left"
        open={open}
        onClose={handleDrawerToggle}
      >
        <List>
          {navItems.map((item, index) => (
            <ListItem disablePadding key={item.path}>
              <ListItemButton
                component={Link}
                to={item.path}
                selected={selectedIndex === index}
                onClick={() => {
                  setSelectedIndex(index);
                  setOpen(false);
                }}
              >
                <ListItemText primary={navBadge(item.path, item.label)} />
              </ListItemButton>
            </ListItem>
          ))}
          <ListItem disablePadding>
            <ListItemButton onClick={handleLogout}>
              <ListItemText primary="Logout" />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
      <main style={{ flexGrow: 1, padding: '24px' }}>
        <Toolbar />
        {children}
      </main>
    </div>
  );
};

export default DrawerMenu;
